"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { useState, useTransition } from "react";
import { ArrowRight, CheckCircle2, LockKeyhole } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { demoPersonas, roleLabels, type AppRole } from "@/lib/phaseone";

export function SignInForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [pending, startTransition] = useTransition();
  const [email, setEmail] = useState(demoPersonas.editor.email);
  const [password, setPassword] = useState("PhaseOneDemo!2026");

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const role = (Object.keys(demoPersonas) as AppRole[]).find((key) => demoPersonas[key].email === email);
    if (!role || password !== "PhaseOneDemo!2026") {
      toast.error("Use one of the synthetic preview identities shown below.");
      return;
    }
    startTransition(async () => {
      const response = await fetch("/api/preview/session", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ role }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({ error: "Sign-in failed." }));
        toast.error(body.error);
        return;
      }
      toast.success(`Signed in as ${roleLabels[role]}`);
      router.push(searchParams.get("next") ?? "/app");
      router.refresh();
    });
  }

  return (
    <form onSubmit={submit} className="grid gap-5">
      <FieldGroup>
        <Field>
          <FieldLabel htmlFor="email">Email</FieldLabel>
          <Input id="email" name="email" type="email" autoComplete="username" value={email} onChange={(event) => setEmail(event.target.value)} required />
        </Field>
        <Field>
          <FieldLabel htmlFor="password">Password</FieldLabel>
          <Input id="password" name="password" type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required />
          <FieldDescription>This password is preview-only and never grants access to client data.</FieldDescription>
        </Field>
      </FieldGroup>
      <Button type="submit" size="lg" disabled={pending} className="w-full justify-between">
        <span className="flex items-center gap-2"><LockKeyhole aria-hidden="true" /> {pending ? "Opening workspace…" : "Open workspace"}</span>
        <ArrowRight aria-hidden="true" />
      </Button>
      <div className="grid gap-2" role="group" aria-label="Preview persona shortcuts">
        {(Object.keys(demoPersonas) as AppRole[]).map((role) => (
          <button
            key={role}
            type="button"
            onClick={() => { setEmail(demoPersonas[role].email); setPassword("PhaseOneDemo!2026"); }}
            className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/35 px-3 py-2.5 text-left transition-colors hover:bg-muted"
          >
            <span>
              <span className="block text-xs font-semibold">{roleLabels[role]}</span>
              <span className="mt-0.5 block text-[0.68rem] text-muted-foreground">{demoPersonas[role].name}</span>
            </span>
            {email === demoPersonas[role].email && <CheckCircle2 className="text-primary" aria-hidden="true" />}
          </button>
        ))}
      </div>
    </form>
  );
}
